import { OrderHistory } from '../../stores/appOrderHistoryStore';
import { RevenueHistory } from '../../services/revenuehistory/revenueHistoryService';
import { CostPriceAnalysis } from '../../services/costPrice/costPriceService';
import { Subrecipe } from '../../services/subrecipes/subrecipesService';
import { InventoryCount } from '../../services/inventory/inventoryService';
import { groupCountsIntoSessions, SessionGroup } from '../../components/pages/inventory/inventoryOverviewUtils';
import { baseProductKey } from '../inventoryProductKey';
import { buildNeededIndex, collectNeedProductInfo } from './utilizationReport';
import {
  inventoryKey,
  latestPriceForKey,
  collectWindowPurchases,
  purchasedQtyForCount,
  neededQtyForCount,
  frozenCountSpace,
  foldIntoCountSpace,
} from './consumptionReport';
import { applySubrecipeExplosion, ExplodableProductAgg } from './inventoryVariance';

/**
 * Pure, React-free computation for the inventory page's "Estimated value" toggle.
 *
 * It projects each GROUP's most-recent inventory count FORWARD to *now*, instead
 * of reconciling two counts (that's the R7 consumption report). For every product
 * counted in the latest session we compute the stock we *should* still hold:
 *
 *   estimatedQty = lastCountedQty + purchasedSinceCount − soldSinceCount
 *
 * where `soldSinceCount` is the menu-derived requirement (R2 needed-index over
 * revenue in [lastCount, now]) — i.e. what sales consumed per ingredient.
 *
 * Estimates are GROUP-level (aggregated across every section in the group); a
 * product living in two sections of the same group is summed, since per-section
 * attribution of restaurant-wide consumption is ambiguous (deferred to prefill).
 *
 * Valuation uses the LIVE latest ordered €/std-unit (ex-VAT), so the figure
 * reflects what the remaining stock is worth right now. Negative estimates (sold
 * more than was available — count error or recipe over-estimate) are flagged and
 * clamped to 0 for the value total.
 *
 * Reuses the inventory-key matching, shared-key re-keying and latest-price walk
 * from `consumptionReport.ts` so the two stay in lock-step.
 */

/** Stable bucket for "one physical group". Legacy/ungrouped counts collapse together. */
function groupKeyOf(session: SessionGroup): string {
  return session.groupId ?? session.groupName ?? 'ungrouped';
}

/** A single product's projected remaining stock within a group. */
export interface EstimatedProductRow {
  key: string;
  productName: string;
  stdUnit: string;
  /**
   * Std-unit quantities. Real numbers except on a product whose sections froze it in unit
   * spaces nothing could bridge (`countQtyUnreadable`) — there is no single quantity to
   * project from, so these are null and must render "—", never 0.
   */
  /** Stock counted at the last session (base std units). */
  startQty: number | null;
  /** Measurable purchases since the count (base units; unmeasurable lines add 0). */
  purchasedQty: number | null;
  /** Menu-derived consumption since the count (base units). */
  neededQty: number | null;
  /** start + purchased − needed (base units; may be < 0). */
  estimatedQty: number | null;
  /** Count unit the product was counted in (e.g. `fles`, `kg`). */
  countUnit: string;
  /** Estimated remaining expressed in count units (e.g. "8 bottles"); null if unknown. */
  estimatedCountQty: number | null;
  /** Cost-price dishes that consume this product (the "used in" hint). */
  dishes: string[];
  /** Live latest ordered €/std-unit. Null = no usable price. */
  pricePerStdUnit: number | null;
  /** max(estimatedQty, 0) × live price. Null when no price. */
  estimatedValue: number | null;
  /** estimatedQty < 0 → likely a count error or recipe over-estimate. */
  negative: boolean;
  /** Some purchases in the window had no quantityInfo → estimate overstated. */
  hasUnmeasurablePurchases: boolean;
  /** Product isn't an ingredient in any sold costed dish → only ever grows. */
  noRecipeCoverage: boolean;
  /** The recipes hold this product in another unit space than the count — its consumption
   *  was converted through the recipe's pack size, or dropped when nothing could convert
   *  it (see `neededQtyForCount`). Either way the product's unit setup is stale. */
  neededUnitMismatch?: boolean;
  /** Two SECTIONS of the counted session froze this product in different unit spaces — the
   *  quantities were rebased through the pack size the frozen items state (`countSpaceBridge`).
   *  OPTIONAL, set only when true. */
  countUnitMismatch?: boolean;
  /** …and nothing could bridge them, so there is no stock figure to project from: every
   *  quantity above is null, as is the estimated value. */
  countQtyUnreadable?: boolean;
  /**
   * Stock for this product came ONLY from exploding a counted subrecipe (prep) into its
   * ingredients — nothing counted it loose, so its projection has no shelf to start from
   * and reads low. There is no confidence concept on this page; the flag is all there is.
   */
  onlyFromSubrecipe: boolean;
}

/** A group's projected stock, anchored on its most recent session. */
export interface EstimatedGroup {
  groupKey: string;
  groupName: string | null;
  /** Window start = the group's latest session timestamp (ms). */
  lastCountedAt: number;
  /** Sum of the latest session's frozen values (the "counted" baseline). */
  countedTotalValue: number;
  /** Sum of clamped estimated values (live-priced). */
  estimatedTotalValue: number;
  products: EstimatedProductRow[];
}

export interface EstimatedInventoryData {
  hasData: boolean;
  groups: EstimatedGroup[];
}

export interface EstimatedInventoryInputs {
  countsHistory: InventoryCount[];
  /** All-time order history (window-filtered here; full list for latest price). */
  orders: OrderHistory[];
  /** All-time revenue history (window-filtered here for the needed-index). */
  revenue: RevenueHistory[];
  costPriceList: CostPriceAnalysis[];
  subrecipesById: Map<string, Subrecipe>;
  defaultWastePercentage: number;
  currentRestaurantId: string;
  /** "Now" — the projection end-point (injected so the util stays pure/testable). */
  now: number;
}

const EMPTY: EstimatedInventoryData = { hasData: false, groups: [] };

/** One counted product's projection inputs. Extends the shared explosion aggregate so a
 *  counted prep expands into its ingredients through the SAME helper — and the same unit
 *  guard — the Leakage tab uses, instead of a second merge that would drift from it. */
export interface CountedProductAgg extends ExplodableProductAgg {
  countUnit: string;
  countFactor: number | null;
  value: number;
  /** Some item summed here was frozen in another unit space than this aggregate's. */
  unitMismatch: boolean;
  /** …and nothing could convert it, so `totalQty` no longer covers every item. */
  qtyUnreadable: boolean;
}

/** Sum a session's count items by inventory key, keeping display + conversion info.
 *  Variant rows (`…__v<n>`) recombine with their base — purchases/needs sit on base keys.
 *
 *  Only ONE session is read here, so there is no opening-vs-closing drift to reconcile —
 *  but a session's SECTIONS are counted independently and each carries its own assignment,
 *  so two of them can still hold this product in different frozen spaces. Same fold, same
 *  rule as the Leakage tab's `aggregateSession`. */
export function sumLatestCounts(counts: InventoryCount[]): Map<string, CountedProductAgg> {
  const map = new Map<string, CountedProductAgg>();
  counts.forEach((c) => {
    c.items.forEach((item) => {
      // Std units per 1 count unit, recovered from the frozen count line.
      const space = frozenCountSpace(item);
      const factor = space.stdPerCountUnit;
      const key = baseProductKey(item.productKey);
      const existing = map.get(key);
      if (existing) {
        const fold = foldIntoCountSpace(
          { totalQty: existing.totalQty, space: { stdUnit: existing.stdUnit, countUnit: existing.countUnit, stdPerCountUnit: existing.countFactor } },
          { qty: item.countInStdUnit, space }
        );
        if (fold.retarget) {
          existing.stdUnit = fold.retarget.stdUnit;
          existing.countUnit = fold.retarget.countUnit;
          existing.countFactor = fold.retarget.stdPerCountUnit;
        }
        if (fold.mismatch) existing.unitMismatch = true;
        if (fold.qty === null) existing.qtyUnreadable = true;
        else existing.totalQty += fold.qty;
        existing.value += item.value;
        if (existing.countFactor == null && factor != null && space.stdUnit === existing.stdUnit) {
          existing.countFactor = factor;
        }
      } else {
        map.set(key, {
          totalQty: item.countInStdUnit,
          name: item.productName,
          stdUnit: space.stdUnit,
          countUnit: space.countUnit,
          countFactor: factor,
          value: item.value,
          unitMismatch: false,
          qtyUnreadable: false,
        });
      }
    });
  });
  return map;
}

export function computeEstimatedInventory(inputs: EstimatedInventoryInputs): EstimatedInventoryData {
  const {
    countsHistory,
    orders,
    revenue,
    costPriceList,
    subrecipesById,
    defaultWastePercentage,
    currentRestaurantId,
    now,
  } = inputs;

  if (countsHistory.length === 0) return EMPTY;

  // Latest session per group (sessions are newest-first → first match wins).
  const sessions = groupCountsIntoSessions(countsHistory);
  const latestByGroup = new Map<string, SessionGroup>();
  sessions.forEach((s) => {
    const key = groupKeyOf(s);
    if (!latestByGroup.has(key)) latestByGroup.set(key, s);
  });

  // Latest-price fallback walks the full order history newest-first.
  const sortedAllOrders = [...orders].sort(
    (a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
  );

  const groups: EstimatedGroup[] = [];

  Array.from(latestByGroup.entries()).forEach(([groupKey, session]) => {
    const startAt = session.completedAt.getTime();
    if (startAt >= now) return; // count is in the future — nothing to project
    const startTotals = sumLatestCounts(session.counts);
    if (startTotals.size === 0) return;

    // A counted prep is not a product to project — its ingredients are. Explode it into
    // them (same helper, same unit guard as the Leakage tab), which drops its own key, so
    // its frozen value has to be carried out FIRST: `countedTotalValue` is accumulated
    // inside the per-product loop below, unlike the leakage page's independent total.
    // Exploded leaves then inherit window purchases, needs and pricing for free.
    let prepCountedValue = 0;
    Array.from(startTotals.entries()).forEach(([key, agg]) => {
      if (key.startsWith('subrecipe:')) prepCountedValue += agg.value;
    });
    applySubrecipeExplosion(
      startTotals,
      { subrecipesById, defaultWastePercentage, currentRestaurantId },
      (identity) => ({
        totalQty: 0,
        name: identity.name,
        stdUnit: identity.stdUnit,
        countUnit: '',
        countFactor: null,
        value: 0,
        unitMismatch: false,
        qtyUnreadable: false,
      })
    );

    // Purchases in [lastCount, now], matched on inventory key.
    // Group universe = counted products.
    const purchases = collectWindowPurchases(orders, startAt, now, (key) => startTotals.has(key));

    // Needed-from-menu over the window (R2 needed-index, re-keyed to inventory keys).
    const windowRevenue = revenue.filter((r) => {
      const t = new Date(r.date).getTime();
      return !Number.isNaN(t) && t >= startAt && t <= now;
    });
    // Window purchases (counted products only) double as the interchangeable-pool
    // supply for alternatives (no-op without any — the engine short-circuits).
    const supply = new Map(Array.from(purchases.entries()).map(([k, v]) => [k, v.baseQty]));
    const sharedNeeded = buildNeededIndex(
      costPriceList,
      windowRevenue,
      subrecipesById,
      defaultWastePercentage,
      currentRestaurantId,
      inventoryKey,
      supply
    );
    const neededByKey = new Map<string, { qty: number; dishes: Set<string> }>();
    Array.from(sharedNeeded.entries()).forEach(([key, info]) => {
      neededByKey.set(key, { qty: info.base, dishes: new Set(info.dishes) });
    });
    // The unit space (and pack size) those needed quantities are denominated in.
    const needInfoByKey = collectNeedProductInfo(costPriceList, subrecipesById, inventoryKey);

    const products: EstimatedProductRow[] = [];
    let estimatedTotalValue = 0;
    // Seeded with the preps' frozen value: their keys are gone, but the stock they stand
    // for was counted and still belongs in the group's counted total.
    let countedTotalValue = prepCountedValue;

    Array.from(startTotals.entries()).forEach(([key, start]) => {
      const buy = purchases.get(key);
      const need = neededByKey.get(key);
      // Read purchases in the space this count was frozen in — see purchasedQtyForCount.
      const { qty: purchasedQty, unitMismatch } = purchasedQtyForCount(buy, start.stdUnit);
      // …and the recipe quantities in the same space — see neededQtyForCount.
      const { qty: neededQty, unitMismatch: neededUnitMismatch } = neededQtyForCount(
        need?.qty ?? 0,
        needInfoByKey.get(key),
        start.stdUnit
      );
      const estimatedQty = start.totalQty + purchasedQty - neededQty;

      // Live price first (what it's worth now); fall back to the frozen count price.
      // The fallback divides the frozen VALUE by the frozen quantity, so it is only a
      // €/std-unit while the two live in one space.
      const unreadable = start.qtyUnreadable;
      const price =
        latestPriceForKey(key, sortedAllOrders) ?? (start.totalQty && !unreadable ? start.value / start.totalQty : null);
      const estimatedValue = price !== null && !unreadable ? Math.max(estimatedQty, 0) * price : null;

      // Frozen values stay real whatever the units did — they are facts of the count.
      countedTotalValue += start.value;
      if (estimatedValue !== null) estimatedTotalValue += estimatedValue;

      products.push({
        key,
        productName: start.name,
        stdUnit: start.stdUnit,
        // Null, never 0 — see the `startQty` contract.
        startQty: unreadable ? null : start.totalQty,
        purchasedQty: unreadable ? null : purchasedQty,
        neededQty: unreadable ? null : neededQty,
        estimatedQty: unreadable ? null : estimatedQty,
        countUnit: start.countUnit,
        estimatedCountQty: start.countFactor && !unreadable ? estimatedQty / start.countFactor : null,
        dishes: need ? Array.from(need.dishes).sort() : [],
        pricePerStdUnit: price,
        estimatedValue,
        negative: !unreadable && estimatedQty < 0,
        hasUnmeasurablePurchases: !!buy?.hadUnmeasurable || unitMismatch,
        // A usage the unit guard dropped is not an absent recipe (same rule as Leakage).
        noRecipeCoverage: neededQty === 0 && !neededUnitMismatch,
        ...(neededUnitMismatch ? { neededUnitMismatch: true } : {}),
        ...(start.unitMismatch ? { countUnitMismatch: true } : {}),
        ...(unreadable ? { countQtyUnreadable: true } : {}),
        onlyFromSubrecipe: !!start.onlyFromSubrecipe,
      });
    });

    // Highest estimated value first.
    products.sort((a, b) => (b.estimatedValue ?? -Infinity) - (a.estimatedValue ?? -Infinity));

    groups.push({
      groupKey,
      groupName: session.groupName,
      lastCountedAt: startAt,
      countedTotalValue,
      estimatedTotalValue,
      products,
    });
  });

  groups.sort((a, b) => (a.groupName ?? '').localeCompare(b.groupName ?? ''));

  return { hasData: groups.length > 0, groups };
}
